import type { ITelemetryItem } from '@microsoft/applicationinsights-web';
import { ApplicationInsights as ApplicationInsightsInitializer } from '@microsoft/applicationinsights-web';
import type { ApplicationInsights } from '@microsoft/applicationinsights-analytics-js/types/applicationinsights-analytics-js';
import type { Initialization } from '@microsoft/applicationinsights-web/dist-esm/Initialization';
import type { ObjectLookup } from 'src/util';

export default class AppInsights {
	constructor(key: string, endpointUrl: string) {
		const initializer = new ApplicationInsightsInitializer({
			config: {
				isCookieUseDisabled: true,
				instrumentationKey: key,
				endpointUrl: endpointUrl,
				enableUnhandledPromiseRejectionTracking: true,
				maxBatchInterval: 10000,
				// if app insights is already globally loaded, avoid doubling up on automatic telemetry collection
				disableExceptionTracking: this.hasGlobalAppInsights,
				disableAjaxTracking: this.hasGlobalAppInsights,
				disableFetchTracking: this.hasGlobalAppInsights
			}
		}) as Initialization;

		initializer.loadAppInsights();

		this.appInsights = initializer.appInsights;
	}

	private appInsights : ApplicationInsights;

	get hasGlobalAppInsights() {
		return !!window['appInsights'];
	}

	// ---------Methods----------
	log(error: Error, additionalProperties: ObjectLookup<string> = null) {
		this.appInsights.trackException({
			id: '',
			exception: error,
			properties: additionalProperties
		});
	}

	pageView(additionalProperties: Record<string, unknown> = null) {
		if (!this.hasGlobalAppInsights)
			this.appInsights.trackPageView({ properties: additionalProperties });
	}

	addTelemetryInitializer(telemetryInitializer: (item: ITelemetryItem) => boolean | void) {
		this.appInsights.addTelemetryInitializer(telemetryInitializer);
	}

	logCustomEvent(eventName: string, additionalProperties: Record<string, unknown> = null) {
		this.appInsights.trackEvent({ name: eventName, properties: additionalProperties });
	}

	logMetric(metricName: string, amount: number, additionalProperties: Record<string, unknown> = null) {
		this.appInsights.trackMetric({
			name: metricName,
			average: amount,
			properties: additionalProperties
		});
	}

	logTrace(message: string, additionalProperties: Record<string, unknown> = null) {
		this.appInsights.trackTrace({
			message,
			properties: additionalProperties
		});
	}

	startTrackEvent(name: string) {
		this.appInsights.startTrackEvent(name);
	}

	stopTrackEvent(name: string, properties: { [key: string]: string }, measurements: { [key: string]: number }) {
		this.appInsights.stopTrackEvent(name, properties, measurements);
	}
}
